import { ChangeDetectionStrategy, Component, Inject, InjectionToken, OnDestroy, OnInit, Optional } from '@angular/core';
import {
    ConfigService,
    DeviceIdService,
    EnvironmentInstanceToken,
    isDefined,
    LinkService,
    MatSerNumber,
    ModalClass,
    NotificationInterface,
    PaceEnvironment,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCircle, faDotCircle } from '@fortawesome/free-regular-svg-icons';
import {
    faCloud,
    faCloudDownloadAlt,
    faCloudUploadAlt,
    faEllipsisV,
    faFilePdf,
    faGlobe,
    faLock,
    faLockOpen,
    faMinusCircle,
    faPencilAlt,
    faSyncAlt,
    faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { NotificationInstanceToken } from '../../core/interfaces/notification.interface';
import { cleanupFormList, loadFormList, setSelectedLanguageInFormList } from '../../store/actions/form-list.actions';
import { abortForm, navigateToForm, refreshXML } from '../../store/actions/form.actions';
import {
    getFormListAvailableLanguagesForNotificationFactory,
    getListForNotificationFactory,
    getSelectedLanguageInListForNotificationFactory,
} from '../../store/selectors/list.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { FormList, FormListEntryState } from '../../types/form-list';
import { FormsConstants } from '../../types/forms-constants';
import { FormUtilities } from '../../utilities/form-utilities';
import { BaseFormsListComponent } from '../base-forms-list/base-forms-list.component';
import FORMS_INSTRUCTIONS_MAPPING = FormsConstants.FORMS_INSTRUCTIONS_MAPPING;
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;

export const FormsListComponentInstanceToken = new InjectionToken<BaseFormsListComponent>('FormsListComponent');

interface ProgressBarUpdate {
    value: number;
    label: string;
}

function getFilteredFormListByLanguage(formList: FormList, selectedLanguage: FormsConstants.FORM_LANGUAGES): FormList {
    let filteredEntries: FormListEntryState[] = [];
    // interims should always be shown, regardless of the selected language
    const englishForms = formList.entries.filter(
        (entry) =>
            entry.loadStatus === FormsConstants.LOAD_STATUS.INTERIM ||
            entry.id.formId.endsWith(FormsConstants.FORM_LANGUAGES.English),
    );

    if (selectedLanguage === FormsConstants.FORM_LANGUAGES.English) {
        filteredEntries = englishForms;
    } else {
        // for other languages than english, add the english forms which are not available in the selectedLanguage
        // and remove the english version of the forms if they are available in the selected language

        // Algorithm
        // 1. filter the language selected forms and all available interims
        // 2. iterate through the filtered language forms
        // 3. remove the english version of the selected language from the english forms array
        // 4. concatenate the 2 form lists
        const selectedLanguageForms = formList.entries.filter(
            (entry) =>
                entry.loadStatus === FormsConstants.LOAD_STATUS.INTERIM || entry.id.formId.endsWith(selectedLanguage),
        );

        selectedLanguageForms.forEach((selectedLanguageForm) => {
            const baseDocumentId = FormUtilities.getFormBaseDocumentId(selectedLanguageForm.id.formId);
            const foundFormIndex = englishForms.findIndex((englishEntry) =>
                englishEntry.id.formId.startsWith(baseDocumentId),
            );
            if (foundFormIndex > -1) {
                englishForms.splice(foundFormIndex, 1);
            }
        });

        filteredEntries = [...selectedLanguageForms, ...englishForms];
    }

    // remove forms in other languages, if a form has already been submitted in a specific language
    const submittedEntries = formList.entries.filter(
        (entry) =>
            entry.submitStatus === FormsConstants.SUBMIT_STATUS.INTERIM ||
            entry.submitStatus === FormsConstants.SUBMIT_STATUS.INTERIM_NOT_OK ||
            entry.submitStatus === FormsConstants.SUBMIT_STATUS.FINAL ||
            entry.submitStatus === FormsConstants.SUBMIT_STATUS.FINAL_NOT_OK,
    );

    submittedEntries.forEach((submittedEntry) => {
        const baseDocumentId = FormUtilities.getFormBaseDocumentId(submittedEntry.id.formId);

        const indexOfFormUnSubmittedInOtherLanguage = filteredEntries.findIndex(
            (filteredEntry) =>
                filteredEntry.id.formId.startsWith(baseDocumentId) &&
                filteredEntry.id.formId !== submittedEntry.id.formId,
        );

        if (indexOfFormUnSubmittedInOtherLanguage > -1) {
            filteredEntries.splice(indexOfFormUnSubmittedInOtherLanguage, 1);
        }
    });

    return {
        ...formList,
        entries: filteredEntries,
    };
}

// remove forms in other languages, if a form has already been submitted in a specific language
function getFilteredAlreadySubmittedFormsInOtherLanguages(formList: FormList): FormList {
    let formListEntries = formList.entries;
    const submittedEntries = formListEntries.filter(
        (entry) =>
            entry.submitStatus === FormsConstants.SUBMIT_STATUS.INTERIM ||
            entry.submitStatus === FormsConstants.SUBMIT_STATUS.INTERIM_NOT_OK ||
            entry.submitStatus === FormsConstants.SUBMIT_STATUS.FINAL ||
            entry.submitStatus === FormsConstants.SUBMIT_STATUS.FINAL_NOT_OK,
    );

    submittedEntries.forEach((submittedEntry) => {
        const baseDocumentId = FormUtilities.getFormBaseDocumentId(submittedEntry.id.formId);

        formListEntries = formListEntries.filter(
            (formListEntry) =>
                formListEntry.id.formId === submittedEntry.id.formId ||
                !formListEntry.id.formId.startsWith(baseDocumentId),
        );
    });

    return {
        ...formList,
        entries: formListEntries,
    };
}

@Component({
    selector: 'pace-forms-list',
    templateUrl: './forms-list.component.html',
    styleUrls: ['./forms-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormsListComponent extends BaseFormsListComponent implements OnInit, OnDestroy {
    public formList$: Observable<FormList>;
    public filteredFormList$: Observable<FormList>;
    public downloadAllProgress$: Observable<ProgressBarUpdate[]>;

    public deleteCacheTrigger$: Subject<void>;
    public destroy$: Subject<void>;

    public formLanguages$: Observable<FormsConstants.FORM_LANGUAGES[]>;
    public selectedLanguage$: Observable<FormsConstants.FORM_LANGUAGES>;
    public selectedLanguageKey$: Observable<string>;

    public faCircle = faCircle;
    public faDotCircle = faDotCircle;
    public faLock = faLock;
    public faUnlock = faLockOpen;
    public faCloud = faCloud;
    public faCloudDownload = faCloudDownloadAlt;
    public faCloudUpload = faCloudUploadAlt;
    public faPencil = faPencilAlt;
    public faFilePdf = faFilePdf;
    public faMinusCircle = faMinusCircle;
    public showDocumentationLink: boolean;
    private baseUrl: string;
    public faSyncAlt = faSyncAlt;
    public showRefreshXMLButton: boolean;
    public showLanguageSelectionDropdown: boolean;
    public showAbortButtonColumn: boolean;
    public userFrontId: string;

    public readonly isMockMode: boolean;
    public constructor(
        private readonly configService: ConfigService,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        @Inject(EnvironmentInstanceToken) private readonly environment: PaceEnvironment,
        private readonly store: Store<FormsState>,
        @Optional() public readonly modalInstance: BsModalRef,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly linkService: LinkService,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
        private readonly deviceIdService: DeviceIdService,
    ) {
        super();

        this.isMockMode = this.environment.operateInMockMode;

        this.deleteCacheTrigger$ = new Subject<void>();
        this.destroy$ = new Subject<void>();
        this.deleteCacheTrigger$
            .asObservable()
            .pipe(
                switchMap(() => this.filteredFormList$),
                take(1),
                takeUntil(this.destroy$),
            )
            .subscribe((formList) => {
                this.store.dispatch(cleanupFormList({ formList }));
            });
    }

    public ngOnInit(): void {
        const notification = this.notificationService.getNotificationStateByIdentifier(this.notificationIdentifier);
        if (typeof notification === 'undefined') {
            throw new Error(
                `FormsListComponent: ngOnInit: Could not load notification model for id '${JSON.stringify(
                    this.notificationIdentifier,
                )}'`,
            );
        }
        const equipment = this.notificationService.getMainEquipmentFromNotification(notification);
        if (typeof equipment === 'undefined') {
            throw new Error(
                `FormsListComponent: ngOnInit: notification '${JSON.stringify(
                    this.notificationIdentifier,
                )}' has no main equipment set!`,
            );
        }
        const equipmentNr = equipment.equnr;
        const materialNr = equipment.matnr;
        const serialNr = equipment.sernr;

        const selectedLanguageSelector = getSelectedLanguageInListForNotificationFactory({
            notification: this.notificationIdentifier,
        });
        this.selectedLanguage$ = this.store.select(selectedLanguageSelector);

        this.selectedLanguageKey$ = this.selectedLanguage$.pipe(
            map((selectedLanguage) => {
                return this.getLanguageKey(selectedLanguage);
            }),
        );

        const getAvailableLanguagesSelector = getFormListAvailableLanguagesForNotificationFactory({
            notification: this.notificationIdentifier,
        });
        this.formLanguages$ = this.store.select(getAvailableLanguagesSelector);

        const getListSelector = getListForNotificationFactory({ notification: this.notificationIdentifier });
        this.formList$ = this.store.select(getListSelector).pipe(filter(isDefined));
        this.filteredFormList$ = this.formList$.pipe(
            withLatestFrom(this.selectedLanguage$),
            map(([formList, selectedLanguage]) => {
                let filteredFormList;

                if (selectedLanguage === FormsConstants.FORM_LANGUAGES.Multilingual) {
                    filteredFormList = formList;
                } else {
                    filteredFormList = getFilteredFormListByLanguage(formList, selectedLanguage);
                }

                return getFilteredAlreadySubmittedFormsInOtherLanguages(filteredFormList);
            }),
        );

        const listLoadingKey = 'SELECT_FORM_LOADING';
        const downloadAllKey = 'SELECT_FORM_DOWNLOADING_ALL';

        const fullPercentage = 100;
        this.downloadAllProgress$ = combineLatest([
            this.filteredFormList$,
            this.translate.stream([listLoadingKey, downloadAllKey]),
        ]).pipe(
            map(([formList, textMessages]: [FormList, { [key: string]: string }]) => {
                const listLoadingMessage = textMessages[listLoadingKey];
                const downloadAllMessage = textMessages[downloadAllKey];

                const value = formList.downloadingAll
                    ? (formList.entries.filter((e) => e.cached).length / formList.entries.length) * fullPercentage
                    : fullPercentage;
                const label = formList.downloadingAll ? downloadAllMessage : listLoadingMessage;

                return [
                    {
                        value,
                        label,
                    },
                ];
            }),
        );

        this.store.dispatch(
            loadFormList({
                equipmentNr,
                mainEquipment: {
                    matnr: materialNr,
                    sernr: serialNr,
                },
                notification: this.notificationIdentifier,
                fromCache: this.useFormListCache,
            }),
        );

        this.baseUrl = this.configService.getInstructionsUrl();
        this.log.debug(
            `FormsListComponent: openInstructions: Instructions Service Link received from config service '${this.baseUrl}'`,
        );
        this.showDocumentationLink = this.configService.getShowDocumentationLink() && !!this.baseUrl;
        this.showRefreshXMLButton = this.configService.getShowRefreshXMLButton();
        this.showLanguageSelectionDropdown = this.configService.getShowLanguageSelectionDropdown();
        this.showAbortButtonColumn = this.configService.getEnableAbortFormInList();
        this.userFrontId = this.configService.getUserFrontId();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public previewForm($event: UIEvent, entry: FormListEntryState): void {
        this.log.debug(`FormsListComponent: previewForm: '${entry.title}'`);

        this.store.dispatch(
            navigateToForm({
                notification: this.notificationIdentifier,
                form: entry.id,
                status:
                    // if the form is already locked for the user, then we open the form in edit mode
                    entry.lockedBy === this.configService.getUserFrontId()
                        ? FormsConstants.FORMSTATUS.LOCK
                        : FormsConstants.FORMSTATUS.PREVIEW,
            }),
        );

        this.hideModal();
    }

    private hideModal(): void {
        if (this.modalInstance) {
            this.modalInstance.hide();
        }
    }

    public editForm($event: UIEvent, entry: FormListEntryState): void {
        this.log.debug(`FormListComponent: editForm '${entry.title}'`);

        // Required to stop "previewForm" from getting called.
        $event.stopPropagation();

        // Submitted entries can no longer be edited, only previewed
        if (this.hasBeenUploaded(entry)) {
            this.log.debug(
                `FormListComponent: editForm '${entry.title}' was already submitted as '${entry.submitStatus}'; not editing it again!`,
            );
            return;
        }

        /**
         * Can be removed in March 2026.
         * The Purpose is to give the device id service information about the device id who locked a form.
         * This is needed to decide whether to return an old legacy device id from local storage, or a new device id using
         * os, vendor and browser information. Only required during the transition period from old legacy device id to
         * the new device id algorithm.
         *
         * After March 2026, this can be changed to only retrieve the new device id: DeviceId.Service.getDeviceId()
         */
        this.deviceIdService.setLegacyDeviceId(entry.deviceId);

        this.store.dispatch(
            navigateToForm({
                notification: this.notificationIdentifier,
                form: entry.id,
                status: FormsConstants.FORMSTATUS.LOCK,
            }),
        );

        this.hideModal();
    }

    public abortForm($event: UIEvent, entry: FormListEntryState, formList: FormList): void {
        $event.stopPropagation();
        this.store.dispatch(
            abortForm({
                form: entry.id,
                formType: entry.type,
                notification: formList.notification,
                navigateToDetails: false,
            }),
        );
    }

    public showAbortFormButtonInList(entry: FormListEntryState, formList: FormList): boolean {
        const supportParticipantCopies = this.configService
            .getParticipantCopySupportByNotificationType(formList.notificationType)
            .includes(entry.type);
        return this.configService.getEnableAbortFormInList() && !supportParticipantCopies;
    }

    public disableAbortButton(entry: FormListEntryState): boolean {
        const hasLock = this.userFrontId === entry.lockedBy;
        const hasBeenSubmitted = [
            SUBMIT_STATUS.HANDOVER,
            SUBMIT_STATUS.INTERIM,
            SUBMIT_STATUS.INTERIM_NOT_OK,
            SUBMIT_STATUS.FINAL,
            SUBMIT_STATUS.FINAL_NOT_OK,
        ].includes(entry.submitStatus);

        return !hasLock || hasBeenSubmitted;
    }

    public refreshXML($event: UIEvent, entry: FormListEntryState): void {
        $event.stopPropagation();
        if (navigator.onLine) {
            const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                backdrop: 'static',
                class: ModalClass.MODAL_MEDIUM,
                initialState: {
                    model: new YesNoModalDialogModel(
                        <string>this.translate.instant('REFRESH_XML_TITLE'),
                        <string>this.translate.instant('REFRESH_XML_BODY'),
                        <string>this.translate.instant('REFRESH_XML_YES_BUTTON'),
                        <string>this.translate.instant('REFRESH_XML_NO_BUTTON'),
                    ),
                },
                ariaLabelledBy: 'yes-no-modal-title',
            });
            modalInstance.content?.modalResult.subscribe((result) => {
                if (result) {
                    const notification = this.notificationService.getNotificationStateByIdentifier(
                        this.notificationIdentifier,
                    );
                    if (typeof notification === 'undefined') {
                        throw new Error(
                            `FormsListComponent: refreshXML: Could not load notification model for id '${JSON.stringify(
                                this.notificationIdentifier,
                            )}'`,
                        );
                    }
                    const equipment = this.notificationService.getMainEquipmentFromNotification(notification);
                    if (typeof equipment === 'undefined') {
                        throw new Error(
                            `FormsListComponent: refreshXML: notification '${JSON.stringify(
                                this.notificationIdentifier,
                            )}' has no main equipment set!`,
                        );
                    }
                    const mainEquipment: MatSerNumber = {
                        matnr: equipment.matnr,
                        sernr: equipment.sernr,
                    };
                    // waiting for 300 ms to get the animation of confirmation modal clear because on next action loading modal
                    // will open up and closing of multiple modals with modal instances won't work.
                    const timeInMs = 300;
                    setTimeout(
                        () =>
                            this.store.dispatch(
                                refreshXML({
                                    form: entry.id,
                                    mainEquipment,
                                    notification: this.notificationIdentifier,
                                    fromCache: this.useFormListCache,
                                }),
                            ),
                        timeInMs,
                    );
                }
            });
        } else {
            this.showInternetRequiredMessage();
        }
    }

    public hasLock(entry: FormListEntryState): boolean {
        return entry.deviceId.length > 0 && entry.lockedBy.length > 0;
    }

    private showInternetRequiredMessage(): void {
        this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    `${this.translate.instant('INTERNET_REQUIRED_TITLE')}`,
                    `${this.translate.instant('INTERNET_REQUIRED_BODY')}`,
                    `${this.translate.instant('INTERNET_REQUIRED_YES_BUTTON')}`,
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });
    }

    /**
     * This function is only used for debugging purposes
     */
    public deleteCachedForms(): void {
        this.deleteCacheTrigger$.next();
    }

    public hasBeenUploaded(entry: FormListEntryState): boolean {
        return [
            SUBMIT_STATUS.FINAL,
            SUBMIT_STATUS.FINAL_NOT_OK,
            SUBMIT_STATUS.INTERIM,
            SUBMIT_STATUS.INTERIM_NOT_OK,
            SUBMIT_STATUS.HANDOVER,
        ].includes(entry.submitStatus);
    }

    public hasBeenLoadedFromInterim(entry: FormListEntryState): boolean {
        return entry.loadStatus === FormsConstants.LOAD_STATUS.INTERIM;
    }

    public hasBeenLoadedFromTemplate(entry: FormListEntryState): boolean {
        return entry.loadStatus === FormsConstants.LOAD_STATUS.TEMPLATE;
    }

    public openInstructions(event: UIEvent, entry: FormListEntryState): void {
        event.stopPropagation();
        const mappedFormType = FORMS_INSTRUCTIONS_MAPPING[entry.type];
        const formNameWithMappedType = this.replaceFormTypeWithMappedType(entry.id.formId, mappedFormType);
        const instructionsLink = `${this.baseUrl}pdf/${mappedFormType}/${formNameWithMappedType}.pdf`;
        this.linkService.openLink(instructionsLink);
    }

    public selectLanguage(language: FormsConstants.FORM_LANGUAGES): void {
        this.store.dispatch(
            setSelectedLanguageInFormList({ notification: this.notificationIdentifier, selectedLanguage: language }),
        );
    }

    public getLanguageKey(selectedLanguage: FormsConstants.FORM_LANGUAGES): string {
        return Object.keys(FormsConstants.FORM_LANGUAGES)[
            Object.values(FormsConstants.FORM_LANGUAGES).indexOf(selectedLanguage)
        ];
    }

    private replaceFormTypeWithMappedType(formDocId: string, newType: string): string {
        const formDocParts = formDocId.split('.');
        // expecting second part would always be form type id
        formDocParts[1] = newType;
        // fifth part is always the language
        // instructions are only available in english so we need to call the english instructions
        formDocParts[4] = FormsConstants.FORM_LANGUAGES.English;
        return formDocParts.join('.');
    }

    protected readonly faGlobe = faGlobe;
    protected readonly faEllipsisV = faEllipsisV;
    protected readonly faTrashAlt = faTrashAlt;
}
